/**
 * Organization Detail Page
 * View and manage organization details, users, API keys, and balances
 */

import { useEffect, useState } from "react"
import { Link, useNavigate, useParams } from "react-router-dom"
import { apiClient } from "../lib/api"
import { formatCurrency, formatDate, getStatusColor } from "../lib/utils"
import type { Currency, OrganizationDetails } from "../types"

export function OrganizationDetailPage() {
  const { orgId } = useParams<{ orgId: string }>()
  const navigate = useNavigate()
  const [orgData, setOrgData] = useState<OrganizationDetails | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Modal states
  const [showAddUserModal, setShowAddUserModal] = useState(false)
  const [showCreateKeyModal, setShowCreateKeyModal] = useState(false)
  const [showAddBalanceModal, setShowAddBalanceModal] = useState(false)
  const [userToRemove, setUserToRemove] = useState<{ id: string; name: string } | null>(null)
  const [keyToRevoke, setKeyToRevoke] = useState<{ id: string; nickname: string } | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)

  // Form states
  const [addUserForm, setAddUserForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    roles: [] as string[],
    password: null,
  })
  const [createKeyForm, setCreateKeyForm] = useState({
    nickname: "",
    scope: "trade:read" as "trade:read" | "trade:write",
  })
  const [createdApiKey, setCreatedApiKey] = useState<{
    id: string
    publicKey: string
    privateKey: string
    nickname: string
  } | null>(null)
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([])
  const [isSharingKey, setIsSharingKey] = useState(false)
  const [addBalanceForm, setAddBalanceForm] = useState({
    currencyId: "",
  })

  // Edit organization states
  const [showEditOrgModal, setShowEditOrgModal] = useState(false)
  const [editOrgForm, setEditOrgForm] = useState({
    creditLimit: 0,
    status: "active" as "active" | "inactive" | "suspended",
    notes: "",
    isTradingAllowed: true,
    requestsPerSec: 10,
  })

  // Edit user states
  const [showEditUserModal, setShowEditUserModal] = useState(false)
  const [userToEdit, setUserToEdit] = useState<{
    id: string
    firstName: string
    lastName: string
    email: string
    status: string
    isTradingAllowed: boolean
  } | null>(null)

  // Reset password states
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false)
  const [userToResetPassword, setUserToResetPassword] = useState<{ id: string; name: string } | null>(null)
  const [newPassword, setNewPassword] = useState("")

  // Common data states
  const [newCurrencies, setNewCurrencies] = useState<Currency[]>([])

  useEffect(() => {
    if (orgId) {
      loadOrganizationDetails()

      apiClient
        .listNewCurrenciesForOrg(orgId)
        .then((data) => {
          setNewCurrencies(data.data.currencies)
        })
        .catch(() => {})
    }
  }, [orgId])

  const loadOrganizationDetails = async () => {
    if (!orgId) return

    setIsLoading(true)
    setError(null)

    try {
      const data = await apiClient.getOrganization(orgId)
      setOrgData(data)
    } catch (err) {
      console.error("Failed to load organization:", err)
      setError(err instanceof Error ? err.message : "Failed to load organization")
    } finally {
      setIsLoading(false)
    }
  }

  const handleAddUser = async () => {
    if (!orgId) return

    try {
      const newUser = await apiClient.addUserToOrg(orgId, addUserForm)
      setSuccessMessage(`User ${newUser.email} added successfully! Password: ${newUser.password}`)
      setShowAddUserModal(false)
      setAddUserForm({ firstName: "", lastName: "", email: "", roles: [], password: "" })
      await loadOrganizationDetails()
    } catch (err) {
      alert(err instanceof Error ? err.message : "Failed to add user")
    }
  }

  const handleRemoveUser = async () => {
    if (!orgId || !userToRemove) return

    try {
      await apiClient.removeUserFromOrg(orgId, userToRemove.id)
      setSuccessMessage(`User ${userToRemove.name} removed successfully`)
      setUserToRemove(null)
      await loadOrganizationDetails()
    } catch (err) {
      alert(err instanceof Error ? err.message : "Failed to remove user")
    }
  }

  const handleCreateApiKey = async () => {
    if (!orgId) return

    try {
      const newKey = await apiClient.createApiKey(orgId, createKeyForm)
      setCreatedApiKey({
        id: newKey.id,
        publicKey: newKey.publicKey,
        privateKey: newKey.privateKey,
        nickname: newKey.nickname,
      })
      setSuccessMessage(`API key "${newKey.nickname}" created successfully`)
      setCreateKeyForm({ nickname: "", scope: "trade:read" })
      await loadOrganizationDetails()
    } catch (err) {
      alert(err instanceof Error ? err.message : "Failed to create API key")
    }
  }

  const handleShareApiKey = async () => {
    if (!orgId || !createdApiKey || selectedUserIds.length === 0) return

    setIsSharingKey(true)
    try {
      const result = await apiClient.shareApiKey(orgId, createdApiKey.id, {
        userIds: selectedUserIds,
        publicKey: createdApiKey.publicKey,
        privateKey: createdApiKey.privateKey,
      })

      const successCount = result.details.filter((d) => d.success).length
      setSuccessMessage(`API key shared with ${successCount} user(s) via email`)
      setSelectedUserIds([])
      setShowCreateKeyModal(false)
      setCreatedApiKey(null)
    } catch (err) {
      alert(err instanceof Error ? err.message : "Failed to share API key")
    } finally {
      setIsSharingKey(false)
    }
  }

  const handleToggleUserSelection = (userId: string) => {
    setSelectedUserIds((prev) => (prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]))
  }

  const handleRevokeApiKey = async () => {
    if (!orgId || !keyToRevoke) return

    try {
      await apiClient.revokeApiKey(orgId, keyToRevoke.id)
      setSuccessMessage(`API key "${keyToRevoke.nickname}" revoked successfully`)
      setKeyToRevoke(null)
      await loadOrganizationDetails()
    } catch (err) {
      alert(err instanceof Error ? err.message : "Failed to revoke API key")
    }
  }

  const handleRoleToggle = (role: string) => {
    setAddUserForm((prev) => ({
      ...prev,
      roles: prev.roles.includes(role) ? prev.roles.filter((r) => r !== role) : [...prev.roles, role],
    }))
  }

  const handleEditOrganization = () => {
    if (!orgData) return
    setEditOrgForm({
      creditLimit: orgData.organization.creditLimit,
      status: orgData.organization.status,
      notes: orgData.organization.notes || "",
      isTradingAllowed: orgData.organization.isTradingAllowed,
      requestsPerSec: orgData.organization.requestsPerSec,
    })
    setShowEditOrgModal(true)
  }

  const handleSaveOrganization = async () => {
    if (!orgId) return
    try {
      await apiClient.updateOrganization(orgId, editOrgForm)
      setSuccessMessage("Organization updated successfully")
      setShowEditOrgModal(false)
      await loadOrganizationDetails()
    } catch (err) {
      alert(err instanceof Error ? err.message : "Failed to update organization")
    }
  }

  const handleEditUser = (user: OrganizationDetails["users"][number]) => {
    setUserToEdit({
      id: user.id,
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      status: user.status,
      isTradingAllowed: true, // Default value, you may need to get this from user data
    })
    setShowEditUserModal(true)
  }

  const handleSaveUser = async () => {
    if (!orgId || !userToEdit) return
    try {
      await apiClient.updateUserInOrg(orgId, userToEdit.id, {
        firstName: userToEdit.firstName,
        lastName: userToEdit.lastName,
        email: userToEdit.email,
        status: userToEdit.status as "active" | "inactive",
        isTradingAllowed: userToEdit.isTradingAllowed,
      })
      setSuccessMessage("User updated successfully")
      setShowEditUserModal(false)
      setUserToEdit(null)
      await loadOrganizationDetails()
    } catch (err) {
      alert(err instanceof Error ? err.message : "Failed to update user")
    }
  }

  const handleResetPassword = (user: OrganizationDetails["users"][number]) => {
    setUserToResetPassword({ id: user.id, name: `${user.firstName} ${user.lastName}` })
    setNewPassword("")
    setShowResetPasswordModal(true)
  }

  const handleConfirmResetPassword = async () => {
    if (!orgId || !userToResetPassword || !newPassword) return
    try {
      const result = await apiClient.resetUserPassword(orgId, userToResetPassword.id, newPassword)
      setSuccessMessage(`Password reset for ${userToResetPassword.name}. New password: ${result.newPassword}`)
      setShowResetPasswordModal(false)
      setUserToResetPassword(null)
      setNewPassword("")
    } catch (err) {
      alert(err instanceof Error ? err.message : "Failed to reset password")
    }
  }

  const handleAddBalance = async () => {
    if (!orgId) return

    try {
      const balance = await apiClient.addBalanceToOrg(orgId, addBalanceForm)
      setSuccessMessage(`${balance.currency} currency balance added successfully to the org.`)
      setShowAddBalanceModal(false)
      setAddBalanceForm({ currencyId: "" })
      await loadOrganizationDetails()
    } catch (err) {
      alert(err instanceof Error ? err.message : "Failed to add currency balance to the org")
    }
  }

  if (isLoading) {
    return (
      <div style={{ padding: "2rem", textAlign: "center" }}>
        <p style={{ color: "#94a3b8", fontSize: "0.9375rem" }}>Loading organization details...</p>
      </div>
    )
  }

  if (error || !orgData) {
    return (
      <div style={{ padding: "2rem", textAlign: "center" }}>
        <p style={{ color: "#f87171", fontSize: "0.9375rem" }}>{error || "Organization not found"}</p>
        <button
          type="button"
          onClick={() => navigate("/organizations")}
          style={{
            marginTop: "1rem",
            padding: "0.5rem 1rem",
            background: "rgba(255, 255, 255, 0.05)",
            border: "1px solid rgba(255, 255, 255, 0.1)",
            borderRadius: "8px",
            color: "#e2e8f0",
            cursor: "pointer",
          }}
        >
          Back to Organizations
        </button>
      </div>
    )
  }

  const { organization, stats, users, apiKeys, balances } = orgData

  const availableRoles = ["ADMIN", "TRADER", "OPERATIONS", "READ_ONLY", "TRADE_OPS"]

  return (
    <div style={{ padding: "2rem" }}>
      {/* Success Message */}
      {successMessage && (
        <div
          style={{
            padding: "1rem",
            marginBottom: "1rem",
            background: "rgba(34, 197, 94, 0.1)",
            border: "1px solid rgba(34, 197, 94, 0.3)",
            borderRadius: "8px",
            color: "#86efac",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <span>{successMessage}</span>
          <button
            type="button"
            onClick={() => setSuccessMessage(null)}
            style={{
              background: "none",
              border: "none",
              color: "#86efac",
              cursor: "pointer",
              fontSize: "1.25rem",
              padding: "0 0.5rem",
            }}
          >
            ×
          </button>
        </div>
      )}

      {/* Header */}
      <div style={{ marginBottom: "2rem" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "1rem", marginBottom: "1rem" }}>
          <Link
            to="/organizations"
            style={{
              color: "#94a3b8",
              textDecoration: "none",
              fontSize: "0.875rem",
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
            }}
          >
            ← Back
          </Link>
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <h1 style={{ fontSize: "1.875rem", fontWeight: 700, color: "#f1f5f9", marginBottom: "0.5rem" }}>
              {organization.name}
            </h1>
            <p style={{ color: "#94a3b8", fontSize: "0.9375rem" }}>
              {organization.displayName && organization.displayName !== organization.name && (
                <span>{organization.displayName} • </span>
              )}
              {organization.billingEmail}
            </p>
          </div>
          <span className={`badge badge-${getStatusColor(organization.status)}`}>{organization.status}</span>
        </div>
      </div>

      {/* Stats Cards */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
          gap: "1rem",
          marginBottom: "2rem",
        }}
      >
        <div className="card" style={{ padding: "1.5rem" }}>
          <p style={{ color: "#94a3b8", fontSize: "0.875rem", marginBottom: "0.5rem" }}>Users</p>
          <p style={{ fontSize: "1.5rem", fontWeight: 700, color: "#f1f5f9" }}>{stats.userCount}</p>
        </div>
        <div className="card" style={{ padding: "1.5rem" }}>
          <p style={{ color: "#94a3b8", fontSize: "0.875rem", marginBottom: "0.5rem" }}>API Keys</p>
          <p style={{ fontSize: "1.5rem", fontWeight: 700, color: "#f1f5f9" }}>{stats.apiKeyCount}</p>
        </div>
        <div className="card" style={{ padding: "1.5rem" }}>
          <p style={{ color: "#94a3b8", fontSize: "0.875rem", marginBottom: "0.5rem" }}>Trades</p>
          <p style={{ fontSize: "1.5rem", fontWeight: 700, color: "#f1f5f9" }}>{stats.tradeCount}</p>
        </div>
        <div className="card" style={{ padding: "1.5rem" }}>
          <p style={{ color: "#94a3b8", fontSize: "0.875rem", marginBottom: "0.5rem" }}>Credit Limit</p>
          <p style={{ fontSize: "1.5rem", fontWeight: 700, color: "#f1f5f9" }}>
            {formatCurrency(organization.creditLimit)}
          </p>
        </div>
      </div>

      {/* Organization Details */}
      <div className="card" style={{ marginBottom: "1.5rem", padding: "1.5rem" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
          <h2 style={{ fontSize: "1.25rem", fontWeight: 600, color: "#e2e8f0" }}>Organization Details</h2>
          <button
            type="button"
            onClick={handleEditOrganization}
            style={{
              padding: "0.5rem 1rem",
              background: "rgba(99, 102, 241, 0.2)",
              border: "1px solid rgba(99, 102, 241, 0.3)",
              borderRadius: "8px",
              color: "#a78bfa",
              fontSize: "0.875rem",
              fontWeight: 500,
              cursor: "pointer",
            }}
          >
            Edit Organization
          </button>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "1rem" }}>
          <div>
            <p style={{ color: "#94a3b8", fontSize: "0.875rem", marginBottom: "0.25rem" }}>Entity Associated</p>
            <p style={{ color: "#e2e8f0" }}>{organization.entityAssociated}</p>
          </div>
          <div>
            <p style={{ color: "#94a3b8", fontSize: "0.875rem", marginBottom: "0.25rem" }}>Account Type</p>
            <p style={{ color: "#e2e8f0" }}>{organization.accountType}</p>
          </div>
          <div>
            <p style={{ color: "#94a3b8", fontSize: "0.875rem", marginBottom: "0.25rem" }}>Credit Used</p>
            <p style={{ color: "#e2e8f0" }}>{formatCurrency(organization.creditUsed)}</p>
          </div>
          <div>
            <p style={{ color: "#94a3b8", fontSize: "0.875rem", marginBottom: "0.25rem" }}>Trading Allowed</p>
            <p style={{ color: "#e2e8f0" }}>{organization.isTradingAllowed ? "Yes" : "No"}</p>
          </div>
          <div>
            <p style={{ color: "#94a3b8", fontSize: "0.875rem", marginBottom: "0.25rem" }}>Created</p>
            <p style={{ color: "#e2e8f0" }}>{formatDate(organization.createdAt)}</p>
          </div>
          {organization.notes && (
            <div style={{ gridColumn: "1 / -1" }}>
              <p style={{ color: "#94a3b8", fontSize: "0.875rem", marginBottom: "0.25rem" }}>Notes</p>
              <p style={{ color: "#e2e8f0" }}>{organization.notes}</p>
            </div>
          )}
        </div>
      </div>

      {/* Users */}
      <div className="card" style={{ marginBottom: "1.5rem" }}>
        <div style={{ padding: "1.5rem", borderBottom: "1px solid rgba(255, 255, 255, 0.05)" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <h2 style={{ fontSize: "1.25rem", fontWeight: 600, color: "#e2e8f0" }}>Users ({users.length})</h2>
            <button
              type="button"
              onClick={() => setShowAddUserModal(true)}
              style={{
                padding: "0.5rem 1rem",
                background: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
                border: "none",
                borderRadius: "8px",
                color: "white",
                fontSize: "0.875rem",
                fontWeight: 500,
                cursor: "pointer",
              }}
            >
              Add User
            </button>
          </div>
        </div>
        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Roles</th>
                <th>Status</th>
                <th>Last Login</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.id}>
                  <td>
                    {user.firstName} {user.lastName}
                  </td>
                  <td>{user.email}</td>
                  <td>
                    {user.roles.map((role) => (
                      <span key={role} className="badge badge-default" style={{ marginRight: "0.5rem" }}>
                        {role}
                      </span>
                    ))}
                  </td>
                  <td>
                    <span className={`badge badge-${getStatusColor(user.status)}`}>{user.status}</span>
                  </td>
                  <td>{user.lastLogin ? formatDate(user.lastLogin) : "Never"}</td>
                  <td>
                    <div style={{ display: "flex", gap: "0.5rem" }}>
                      <button
                        type="button"
                        onClick={() => handleEditUser(user)}
                        style={{
                          padding: "0.25rem 0.75rem",
                          background: "rgba(99, 102, 241, 0.2)",
                          border: "1px solid rgba(99, 102, 241, 0.3)",
                          borderRadius: "6px",
                          color: "#a78bfa",
                          fontSize: "0.8125rem",
                          fontWeight: 500,
                          cursor: "pointer",
                        }}
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        onClick={() => handleResetPassword(user)}
                        style={{
                          padding: "0.25rem 0.75rem",
                          background: "rgba(234, 179, 8, 0.2)",
                          border: "1px solid rgba(234, 179, 8, 0.3)",
                          borderRadius: "6px",
                          color: "#fde047",
                          fontSize: "0.8125rem",
                          fontWeight: 500,
                          cursor: "pointer",
                        }}
                      >
                        Reset Password
                      </button>
                      <button
                        type="button"
                        onClick={() => setUserToRemove({ id: user.id, name: `${user.firstName} ${user.lastName}` })}
                        style={{
                          padding: "0.25rem 0.75rem",
                          background: "rgba(239, 68, 68, 0.1)",
                          border: "1px solid rgba(239, 68, 68, 0.3)",
                          borderRadius: "6px",
                          color: "#fca5a5",
                          fontSize: "0.8125rem",
                          fontWeight: 500,
                          cursor: "pointer",
                        }}
                      >
                        Remove
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* API Keys */}
      <div className="card" style={{ marginBottom: "1.5rem" }}>
        <div style={{ padding: "1.5rem", borderBottom: "1px solid rgba(255, 255, 255, 0.05)" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <h2 style={{ fontSize: "1.25rem", fontWeight: 600, color: "#e2e8f0" }}>API Keys ({apiKeys.length})</h2>
            <button
              type="button"
              onClick={() => setShowCreateKeyModal(true)}
              style={{
                padding: "0.5rem 1rem",
                background: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
                border: "none",
                borderRadius: "8px",
                color: "white",
                fontSize: "0.875rem",
                fontWeight: 500,
                cursor: "pointer",
              }}
            >
              Create API Key
            </button>
          </div>
        </div>
        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>Nickname</th>
                <th>Scope</th>
                <th>Status</th>
                <th>Last Used</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {apiKeys.map((key) => (
                <tr key={key.id}>
                  <td>{key.nickname || "Unnamed"}</td>
                  <td>
                    <span className="badge badge-default">{key.scope}</span>
                  </td>
                  <td>
                    <span className={`badge badge-${getStatusColor(key.status)}`}>{key.status}</span>
                  </td>
                  <td>{key.lastUsedAt ? formatDate(key.lastUsedAt) : "Never"}</td>
                  <td>{formatDate(key.createdAt)}</td>
                  <td>
                    {key.status === "active" ? (
                      <button
                        type="button"
                        onClick={() => setKeyToRevoke({ id: key.id, nickname: key.nickname || "Unnamed" })}
                        style={{
                          padding: "0.25rem 0.75rem",
                          background: "rgba(239, 68, 68, 0.1)",
                          border: "1px solid rgba(239, 68, 68, 0.3)",
                          borderRadius: "6px",
                          color: "#fca5a5",
                          fontSize: "0.8125rem",
                          cursor: "pointer",
                        }}
                      >
                        Revoke
                      </button>
                    ) : (
                      <span style={{ color: "#64748b", fontSize: "0.8125rem" }}>Revoked</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Balances */}
      <div className="card">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
          <h2 style={{ fontSize: "1.25rem", fontWeight: 600, color: "#e2e8f0" }}>Balances ({balances.length})</h2>
          <button
            type="button"
            onClick={() => setShowAddBalanceModal(true)}
            style={{
              padding: "0.5rem 1rem",
              background: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
              border: "none",
              borderRadius: "8px",
              color: "white",
              fontSize: "0.875rem",
              fontWeight: 500,
              cursor: "pointer",
            }}
          >
            Add Balance
          </button>
        </div>
        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>Currency</th>
                <th>Type</th>
                <th>Amount</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {balances.map((balance) => (
                <tr key={balance.id}>
                  <td>
                    <strong>{balance.currency}</strong>
                  </td>
                  <td>
                    <span className="badge badge-default">{balance.currencyType}</span>
                  </td>
                  <td>{balance.amountNumeric}</td>
                  <td>
                    <span className={`badge badge-${balance.enabled ? "success" : "default"}`}>
                      {balance.enabled ? "Enabled" : "Disabled"}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add Balance Modal */}
      {showAddBalanceModal && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0, 0, 0, 0.7)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 50,
          }}
        >
          <div
            style={{
              background: "#1e293b",
              borderRadius: "12px",
              padding: "2rem",
              width: "50%",
              maxWidth: "75%",
              maxHeight: "75%",
              overflowY: "auto",
              border: "1px solid rgba(255, 255, 255, 0.1)",
            }}
          >
            <h2 style={{ fontSize: "1.5rem", fontWeight: 600, color: "#f1f5f9", marginBottom: "1.5rem" }}>
              Add Balance
            </h2>
            {newCurrencies.length === 0 ? (
              <p style={{ color: "#94a3b8", fontSize: "0.9375rem", textAlign: "center" }}>
                No currencies available to add.
              </p>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
                <div>
                  <label
                    htmlFor="currency"
                    style={{
                      display: "block",
                      marginBottom: "0.5rem",
                      fontSize: "0.875rem",
                      fontWeight: 500,
                      color: "#94a3b8",
                    }}
                  >
                    Select Currency
                  </label>
                  <select
                    id="currency"
                    value={addBalanceForm.currencyId}
                    onChange={(e) => setAddBalanceForm({ currencyId: e.target.value })}
                    style={{
                      width: "100%",
                    }}
                  >
                    <option value="">Select a currency</option>
                    {newCurrencies.map((currency) => (
                      <option key={currency.id} value={currency.id}>
                        {currency.symbol}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}
            <div style={{ display: "flex", gap: "1rem", marginTop: "1.5rem" }}>
              <button
                type="button"
                onClick={() => {
                  setShowAddBalanceModal(false)
                  setAddBalanceForm({ currencyId: "" })
                }}
                style={{
                  flex: 1,
                  padding: "0.75rem",
                  background: "rgba(255, 255, 255, 0.05)",
                  border: "1px solid rgba(255, 255, 255, 0.1)",
                  borderRadius: "8px",
                  color: "#e2e8f0",
                  fontSize: "0.875rem",
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleAddBalance}
                disabled={!addBalanceForm.currencyId || newCurrencies.length === 0}
                style={{
                  flex: 1,
                  padding: "0.75rem",
                  background:
                    !addBalanceForm.currencyId || newCurrencies.length === 0
                      ? "rgba(99, 102, 241, 0.3)"
                      : "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
                  border: "none",
                  borderRadius: "8px",
                  color: "white",
                  fontSize: "0.875rem",
                  fontWeight: 500,
                  cursor: !addBalanceForm.currencyId || newCurrencies.length === 0 ? "not-allowed" : "pointer",
                }}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add User Modal */}
      {showAddUserModal && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0, 0, 0, 0.7)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 50,
          }}
        >
          <div
            style={{
              background: "#1e293b",
              borderRadius: "12px",
              padding: "2rem",
              maxWidth: "75%",
              maxHeight: "75%",
              overflowY: "auto",
              border: "1px solid rgba(255, 255, 255, 0.1)",
            }}
          >
            <h2 style={{ fontSize: "1.5rem", fontWeight: 600, color: "#f1f5f9", marginBottom: "1.5rem" }}>Add User</h2>
            <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
              <div>
                <label
                  htmlFor="add-user-first-name"
                  style={{ display: "block", color: "#94a3b8", fontSize: "0.875rem", marginBottom: "0.5rem" }}
                >
                  First Name *
                </label>
                <input
                  id="add-user-first-name"
                  type="text"
                  value={addUserForm.firstName}
                  onChange={(e) => setAddUserForm({ ...addUserForm, firstName: e.target.value })}
                  style={{
                    width: "100%",
                    padding: "0.5rem",
                    background: "rgba(255, 255, 255, 0.05)",
                    border: "1px solid rgba(255, 255, 255, 0.1)",
                    borderRadius: "6px",
                    color: "#e2e8f0",
                  }}
                />
              </div>
              <div>
                <label
                  htmlFor="add-user-last-name"
                  style={{ display: "block", color: "#94a3b8", fontSize: "0.875rem", marginBottom: "0.5rem" }}
                >
                  Last Name *
                </label>
                <input
                  id="add-user-last-name"
                  type="text"
                  value={addUserForm.lastName}
                  onChange={(e) => setAddUserForm({ ...addUserForm, lastName: e.target.value })}
                  style={{
                    width: "100%",
                    padding: "0.5rem",
                    background: "rgba(255, 255, 255, 0.05)",
                    border: "1px solid rgba(255, 255, 255, 0.1)",
                    borderRadius: "6px",
                    color: "#e2e8f0",
                  }}
                />
              </div>
              <div>
                <label
                  htmlFor="add-user-email"
                  style={{ display: "block", color: "#94a3b8", fontSize: "0.875rem", marginBottom: "0.5rem" }}
                >
                  Email *
                </label>
                <input
                  id="add-user-email"
                  type="email"
                  value={addUserForm.email}
                  onChange={(e) => setAddUserForm({ ...addUserForm, email: e.target.value })}
                  style={{
                    width: "100%",
                    padding: "0.5rem",
                    background: "rgba(255, 255, 255, 0.05)",
                    border: "1px solid rgba(255, 255, 255, 0.1)",
                    borderRadius: "6px",
                    color: "#e2e8f0",
                  }}
                />
              </div>
              <div>
                <label
                  htmlFor="add-user-password"
                  style={{ display: "block", color: "#94a3b8", fontSize: "0.875rem", marginBottom: "0.5rem" }}
                >
                  Password (optional)
                </label>
                <input
                  id="add-user-password"
                  type="text"
                  value={addUserForm.password}
                  onChange={(e) => setAddUserForm({ ...addUserForm, password: e.target.value || null })}
                  placeholder="Leave empty for default: ofx123"
                  style={{
                    width: "100%",
                    padding: "0.5rem",
                    background: "rgba(255, 255, 255, 0.05)",
                    border: "1px solid rgba(255, 255, 255, 0.1)",
                    borderRadius: "6px",
                    color: "#e2e8f0",
                  }}
                />
              </div>
              <div>
                <div style={{ display: "block", color: "#94a3b8", fontSize: "0.875rem", marginBottom: "0.5rem" }}>
                  Roles * (select at least one)
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem" }}>
                  {availableRoles.map((role) => (
                    <button
                      type="button"
                      key={role}
                      onClick={() => handleRoleToggle(role)}
                      style={{
                        padding: "0.5rem 1rem",
                        background: addUserForm.roles.includes(role)
                          ? "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)"
                          : "rgba(255, 255, 255, 0.05)",
                        border: `1px solid ${addUserForm.roles.includes(role) ? "#6366f1" : "rgba(255, 255, 255, 0.1)"}`,
                        borderRadius: "6px",
                        color: addUserForm.roles.includes(role) ? "white" : "#94a3b8",
                        fontSize: "0.875rem",
                        cursor: "pointer",
                      }}
                    >
                      {role}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div style={{ display: "flex", gap: "1rem", marginTop: "1.5rem" }}>
              <button
                type="button"
                onClick={() => {
                  setShowAddUserModal(false)
                  setAddUserForm({ firstName: "", lastName: "", email: "", roles: [], password: "" })
                }}
                style={{
                  flex: 1,
                  padding: "0.75rem",
                  background: "rgba(255, 255, 255, 0.05)",
                  border: "1px solid rgba(255, 255, 255, 0.1)",
                  borderRadius: "8px",
                  color: "#e2e8f0",
                  fontSize: "0.875rem",
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleAddUser}
                disabled={
                  !addUserForm.firstName ||
                  !addUserForm.lastName ||
                  !addUserForm.email ||
                  addUserForm.roles.length === 0
                }
                style={{
                  flex: 1,
                  padding: "0.75rem",
                  background:
                    !addUserForm.firstName ||
                    !addUserForm.lastName ||
                    !addUserForm.email ||
                    addUserForm.roles.length === 0
                      ? "rgba(99, 102, 241, 0.3)"
                      : "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
                  border: "none",
                  borderRadius: "8px",
                  color: "white",
                  fontSize: "0.875rem",
                  fontWeight: 500,
                  cursor:
                    !addUserForm.firstName ||
                    !addUserForm.lastName ||
                    !addUserForm.email ||
                    addUserForm.roles.length === 0
                      ? "not-allowed"
                      : "pointer",
                }}
              >
                Add User
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create API Key Modal */}
      {showCreateKeyModal && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0, 0, 0, 0.7)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 50,
          }}
        >
          <div
            style={{
              background: "#1e293b",
              borderRadius: "12px",
              padding: "2rem",
              width: "500px",
              maxWidth: "75%",
              maxHeight: "75%",
              overflowY: "auto",
              border: "1px solid rgba(255, 255, 255, 0.1)",
            }}
          >
            {createdApiKey ? (
              <>
                <h2 style={{ fontSize: "1.5rem", fontWeight: 600, color: "#f1f5f9", marginBottom: "1.5rem" }}>
                  API Key Created Successfully
                </h2>
                <div
                  style={{
                    padding: "1rem",
                    background: "rgba(34, 197, 94, 0.1)",
                    border: "1px solid rgba(34, 197, 94, 0.3)",
                    borderRadius: "8px",
                    marginBottom: "1rem",
                  }}
                >
                  <p style={{ color: "#86efac", fontSize: "0.875rem", marginBottom: "0.5rem" }}>
                    ⚠️ Save these keys now. The private key will not be shown again.
                  </p>
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
                  <div>
                    <div style={{ display: "block", color: "#94a3b8", fontSize: "0.875rem", marginBottom: "0.5rem" }}>
                      Nickname
                    </div>
                    <p style={{ color: "#e2e8f0", fontWeight: 500 }}>{createdApiKey.nickname}</p>
                  </div>
                  <div>
                    <div style={{ display: "block", color: "#94a3b8", fontSize: "0.875rem", marginBottom: "0.5rem" }}>
                      Public Key
                    </div>
                    <pre
                      style={{
                        padding: "0.75rem",
                        background: "rgba(0, 0, 0, 0.3)",
                        borderRadius: "6px",
                        fontFamily: "monospace",
                        fontSize: "0.75rem",
                        color: "#e2e8f0",
                        whiteSpace: "pre",
                      }}
                    >
                      {createdApiKey.publicKey}
                    </pre>
                  </div>
                  <div>
                    <div style={{ display: "block", color: "#94a3b8", fontSize: "0.875rem", marginBottom: "0.5rem" }}>
                      Private Key
                    </div>
                    <pre
                      style={{
                        padding: "0.75rem",
                        background: "rgba(239, 68, 68, 0.1)",
                        border: "1px solid rgba(239, 68, 68, 0.3)",
                        borderRadius: "6px",
                        fontFamily: "monospace",
                        fontSize: "0.75rem",
                        color: "#fca5a5",
                        whiteSpace: "pre",
                      }}
                    >
                      {createdApiKey.privateKey}
                    </pre>
                  </div>

                  {/* Share with users */}
                  <div
                    style={{ marginTop: "2rem", paddingTop: "1.5rem", borderTop: "1px solid rgba(255, 255, 255, 0.1)" }}
                  >
                    <h3 style={{ fontSize: "1.125rem", fontWeight: 600, color: "#e2e8f0", marginBottom: "1rem" }}>
                      📧 Share via Email
                    </h3>
                    <p style={{ color: "#94a3b8", fontSize: "0.875rem", marginBottom: "1rem" }}>
                      Select users to receive this API key via email
                    </p>
                    <div
                      style={{
                        maxHeight: "200px",
                        overflowY: "auto",
                        background: "rgba(0, 0, 0, 0.2)",
                        borderRadius: "6px",
                        padding: "0.75rem",
                        marginBottom: "1rem",
                      }}
                    >
                      {users.length === 0 ? (
                        <p style={{ color: "#64748b", fontSize: "0.875rem", textAlign: "center", padding: "1rem" }}>
                          No users in this organization
                        </p>
                      ) : (
                        users.map((user) => (
                          <label
                            key={user.id}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              padding: "0.75rem",
                              marginBottom: "0.5rem",
                              background: selectedUserIds.includes(user.id) ? "rgba(99, 102, 241, 0.2)" : "transparent",
                              border: `1px solid ${selectedUserIds.includes(user.id) ? "#6366f1" : "rgba(255, 255, 255, 0.05)"}`,
                              borderRadius: "6px",
                              cursor: "pointer",
                              transition: "all 0.2s",
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={selectedUserIds.includes(user.id)}
                              onChange={() => handleToggleUserSelection(user.id)}
                              style={{ marginRight: "0.75rem", cursor: "pointer" }}
                            />
                            <div style={{ flex: 1 }}>
                              <div style={{ color: "#e2e8f0", fontWeight: 500 }}>
                                {user.firstName} {user.lastName}
                              </div>
                              <div style={{ color: "#94a3b8", fontSize: "0.8125rem" }}>{user.email}</div>
                            </div>
                          </label>
                        ))
                      )}
                    </div>
                  </div>
                </div>

                <div style={{ display: "flex", gap: "1rem", marginTop: "1.5rem" }}>
                  <button
                    type="button"
                    onClick={() => {
                      setShowCreateKeyModal(false)
                      setCreatedApiKey(null)
                      setSelectedUserIds([])
                      setCreateKeyForm({ nickname: "", scope: "trade:read" })
                    }}
                    style={{
                      flex: 1,
                      padding: "0.75rem",
                      background: "rgba(255, 255, 255, 0.05)",
                      border: "1px solid rgba(255, 255, 255, 0.1)",
                      borderRadius: "8px",
                      color: "#e2e8f0",
                      fontSize: "0.875rem",
                      cursor: "pointer",
                    }}
                  >
                    Close
                  </button>
                  <button
                    type="button"
                    onClick={handleShareApiKey}
                    disabled={selectedUserIds.length === 0 || isSharingKey}
                    style={{
                      flex: 1,
                      padding: "0.75rem",
                      background:
                        selectedUserIds.length === 0 || isSharingKey
                          ? "rgba(99, 102, 241, 0.3)"
                          : "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
                      border: "none",
                      borderRadius: "8px",
                      color: "white",
                      fontSize: "0.875rem",
                      fontWeight: 500,
                      cursor: selectedUserIds.length === 0 || isSharingKey ? "not-allowed" : "pointer",
                    }}
                  >
                    {isSharingKey ? "Sending..." : `Share with ${selectedUserIds.length} user(s)`}
                  </button>
                </div>
              </>
            ) : (
              <>
                <h2 style={{ fontSize: "1.5rem", fontWeight: 600, color: "#f1f5f9", marginBottom: "1.5rem" }}>
                  Create API Key
                </h2>
                <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
                  <div>
                    <label
                      htmlFor="create-key-nickname"
                      style={{ display: "block", color: "#94a3b8", fontSize: "0.875rem", marginBottom: "0.5rem" }}
                    >
                      Nickname *
                    </label>
                    <input
                      id="create-key-nickname"
                      type="text"
                      value={createKeyForm.nickname}
                      onChange={(e) => setCreateKeyForm({ ...createKeyForm, nickname: e.target.value })}
                      style={{
                        width: "100%",
                        padding: "0.5rem",
                        background: "rgba(255, 255, 255, 0.05)",
                        border: "1px solid rgba(255, 255, 255, 0.1)",
                        borderRadius: "6px",
                        color: "#e2e8f0",
                      }}
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="create-key-scope"
                      style={{ display: "block", color: "#94a3b8", fontSize: "0.875rem", marginBottom: "0.5rem" }}
                    >
                      Scope *
                    </label>
                    <select
                      id="create-key-scope"
                      value={createKeyForm.scope}
                      onChange={(e) =>
                        setCreateKeyForm({ ...createKeyForm, scope: e.target.value as "trade:read" | "trade:write" })
                      }
                      style={{
                        width: "100%",
                        padding: "0.5rem",
                        background: "rgba(255, 255, 255, 0.05)",
                        border: "1px solid rgba(255, 255, 255, 0.1)",
                        borderRadius: "6px",
                        color: "#e2e8f0",
                      }}
                    >
                      <option value="trade:read">Read Only</option>
                      <option value="trade:write">Read & Write</option>
                    </select>
                  </div>
                </div>
                <div style={{ display: "flex", gap: "1rem", marginTop: "1.5rem" }}>
                  <button
                    type="button"
                    onClick={() => {
                      setShowCreateKeyModal(false)
                      setCreateKeyForm({ nickname: "", scope: "trade:read" })
                    }}
                    style={{
                      flex: 1,
                      padding: "0.75rem",
                      background: "rgba(255, 255, 255, 0.05)",
                      border: "1px solid rgba(255, 255, 255, 0.1)",
                      borderRadius: "8px",
                      color: "#e2e8f0",
                      fontSize: "0.875rem",
                      cursor: "pointer",
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleCreateApiKey}
                    disabled={!createKeyForm.nickname}
                    style={{
                      flex: 1,
                      padding: "0.75rem",
                      background: !createKeyForm.nickname
                        ? "rgba(99, 102, 241, 0.3)"
                        : "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
                      border: "none",
                      borderRadius: "8px",
                      color: "white",
                      fontSize: "0.875rem",
                      fontWeight: 500,
                      cursor: !createKeyForm.nickname ? "not-allowed" : "pointer",
                    }}
                  >
                    Create
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Remove User Confirmation */}
      {userToRemove && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0, 0, 0, 0.7)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 50,
          }}
        >
          <div
            style={{
              background: "#1e293b",
              borderRadius: "12px",
              padding: "2rem",
              width: "500px",
              maxWidth: "75%",
              maxHeight: "75%",
              overflowY: "auto",
              border: "1px solid rgba(255, 255, 255, 0.1)",
            }}
          >
            <h2 style={{ fontSize: "1.5rem", fontWeight: 600, color: "#f1f5f9", marginBottom: "1rem" }}>Remove User</h2>
            <p style={{ color: "#94a3b8", marginBottom: "1.5rem" }}>
              Are you sure you want to remove <strong style={{ color: "#e2e8f0" }}>{userToRemove.name}</strong> from
              this organization?
            </p>
            <div style={{ display: "flex", gap: "1rem" }}>
              <button
                type="button"
                onClick={() => setUserToRemove(null)}
                style={{
                  flex: 1,
                  padding: "0.75rem",
                  background: "rgba(255, 255, 255, 0.05)",
                  border: "1px solid rgba(255, 255, 255, 0.1)",
                  borderRadius: "8px",
                  color: "#e2e8f0",
                  fontSize: "0.875rem",
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleRemoveUser}
                style={{
                  flex: 1,
                  padding: "0.75rem",
                  background: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
                  border: "none",
                  borderRadius: "8px",
                  color: "white",
                  fontSize: "0.875rem",
                  fontWeight: 500,
                  cursor: "pointer",
                }}
              >
                Remove
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Revoke API Key Confirmation */}
      {keyToRevoke && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0, 0, 0, 0.7)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 50,
          }}
        >
          <div
            style={{
              background: "#1e293b",
              borderRadius: "12px",
              padding: "2rem",
              width: "500px",
              maxWidth: "75%",
              maxHeight: "75%",
              overflowY: "auto",
              border: "1px solid rgba(255, 255, 255, 0.1)",
            }}
          >
            <h2 style={{ fontSize: "1.5rem", fontWeight: 600, color: "#f1f5f9", marginBottom: "1rem" }}>
              Revoke API Key
            </h2>
            <p style={{ color: "#94a3b8", marginBottom: "1.5rem" }}>
              Are you sure you want to revoke the API key{" "}
              <strong style={{ color: "#e2e8f0" }}>"{keyToRevoke.nickname}"</strong>? This action cannot be undone.
            </p>
            <div style={{ display: "flex", gap: "1rem" }}>
              <button
                type="button"
                onClick={() => setKeyToRevoke(null)}
                style={{
                  flex: 1,
                  padding: "0.75rem",
                  background: "rgba(255, 255, 255, 0.05)",
                  border: "1px solid rgba(255, 255, 255, 0.1)",
                  borderRadius: "8px",
                  color: "#e2e8f0",
                  fontSize: "0.875rem",
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleRevokeApiKey}
                style={{
                  flex: 1,
                  padding: "0.75rem",
                  background: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
                  border: "none",
                  borderRadius: "8px",
                  color: "white",
                  fontSize: "0.875rem",
                  fontWeight: 500,
                  cursor: "pointer",
                }}
              >
                Revoke
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Organization Modal */}
      {showEditOrgModal && orgData && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0, 0, 0, 0.7)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
          onClick={() => setShowEditOrgModal(false)}
          onKeyDown={(e) => {
            if (e.key === "Escape") setShowEditOrgModal(false)
          }}
        >
          <div
            style={{
              background: "rgba(15, 23, 42, 0.95)",
              padding: "2rem",
              borderRadius: "12px",
              width: "500px",
              maxWidth: "75%",
              maxHeight: "75%",
              overflowY: "auto",
              border: "1px solid rgba(255, 255, 255, 0.1)",
            }}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          >
            <h2 style={{ fontSize: "1.5rem", fontWeight: 600, color: "#f1f5f9", marginBottom: "1.5rem" }}>
              Edit Organization
            </h2>
            <div style={{ display: "flex", flexDirection: "column", gap: "1rem", marginBottom: "1.5rem" }}>
              <div>
                <label
                  htmlFor="edit-org-credit-limit"
                  style={{ display: "block", color: "#94a3b8", fontSize: "0.875rem", marginBottom: "0.5rem" }}
                >
                  Credit Limit
                </label>
                <input
                  id="edit-org-credit-limit"
                  type="number"
                  value={editOrgForm.creditLimit}
                  onChange={(e) => setEditOrgForm({ ...editOrgForm, creditLimit: Number(e.target.value) })}
                  style={{
                    width: "100%",
                    padding: "0.5rem",
                    background: "rgba(255, 255, 255, 0.05)",
                    border: "1px solid rgba(255, 255, 255, 0.1)",
                    borderRadius: "6px",
                    color: "#e2e8f0",
                  }}
                />
              </div>
              <div>
                <label
                  htmlFor="edit-org-requests-per-sec"
                  style={{ display: "block", color: "#94a3b8", fontSize: "0.875rem", marginBottom: "0.5rem" }}
                >
                  Allowed Req / Sec
                </label>
                <input
                  id="edit-org-requests-per-sec"
                  type="number"
                  value={editOrgForm.requestsPerSec}
                  onChange={(e) => setEditOrgForm({ ...editOrgForm, requestsPerSec: Number(e.target.value) })}
                  style={{
                    width: "100%",
                    padding: "0.5rem",
                    background: "rgba(255, 255, 255, 0.05)",
                    border: "1px solid rgba(255, 255, 255, 0.1)",
                    borderRadius: "6px",
                    color: "#e2e8f0",
                  }}
                />
              </div>
              <div>
                <label
                  htmlFor="edit-org-status"
                  style={{ display: "block", color: "#94a3b8", fontSize: "0.875rem", marginBottom: "0.5rem" }}
                >
                  Status
                </label>
                <select
                  id="edit-org-status"
                  value={editOrgForm.status}
                  onChange={(e) =>
                    setEditOrgForm({ ...editOrgForm, status: e.target.value as "active" | "inactive" | "suspended" })
                  }
                  style={{
                    width: "100%",
                    padding: "0.5rem",
                    background: "rgba(255, 255, 255, 0.05)",
                    border: "1px solid rgba(255, 255, 255, 0.1)",
                    borderRadius: "6px",
                    color: "#e2e8f0",
                  }}
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="suspended">Suspended</option>
                </select>
              </div>
              <div>
                <label
                  htmlFor="edit-org-trading"
                  style={{ display: "block", color: "#94a3b8", fontSize: "0.875rem", marginBottom: "0.5rem" }}
                >
                  Trading Allowed
                </label>
                <select
                  id="edit-org-trading"
                  value={editOrgForm.isTradingAllowed ? "yes" : "no"}
                  onChange={(e) => setEditOrgForm({ ...editOrgForm, isTradingAllowed: e.target.value === "yes" })}
                  style={{
                    width: "100%",
                    padding: "0.5rem",
                    background: "rgba(255, 255, 255, 0.05)",
                    border: "1px solid rgba(255, 255, 255, 0.1)",
                    borderRadius: "6px",
                    color: "#e2e8f0",
                  }}
                >
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                </select>
              </div>
              <div>
                <label
                  htmlFor="edit-org-notes"
                  style={{ display: "block", color: "#94a3b8", fontSize: "0.875rem", marginBottom: "0.5rem" }}
                >
                  Notes
                </label>
                <textarea
                  id="edit-org-notes"
                  value={editOrgForm.notes}
                  onChange={(e) => setEditOrgForm({ ...editOrgForm, notes: e.target.value })}
                  rows={3}
                  style={{
                    width: "100%",
                    padding: "0.5rem",
                    background: "rgba(255, 255, 255, 0.05)",
                    border: "1px solid rgba(255, 255, 255, 0.1)",
                    borderRadius: "6px",
                    color: "#e2e8f0",
                    resize: "vertical",
                  }}
                />
              </div>
            </div>
            <div style={{ display: "flex", gap: "1rem" }}>
              <button
                type="button"
                onClick={() => setShowEditOrgModal(false)}
                style={{
                  flex: 1,
                  padding: "0.75rem",
                  background: "rgba(255, 255, 255, 0.05)",
                  border: "1px solid rgba(255, 255, 255, 0.1)",
                  borderRadius: "8px",
                  color: "#e2e8f0",
                  fontSize: "0.875rem",
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveOrganization}
                style={{
                  flex: 1,
                  padding: "0.75rem",
                  background: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
                  border: "none",
                  borderRadius: "8px",
                  color: "white",
                  fontSize: "0.875rem",
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {showEditUserModal && userToEdit && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0, 0, 0, 0.7)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
          onClick={() => setShowEditUserModal(false)}
          onKeyDown={(e) => {
            if (e.key === "Escape") setShowEditUserModal(false)
          }}
        >
          <div
            style={{
              background: "rgba(15, 23, 42, 0.95)",
              padding: "2rem",
              borderRadius: "12px",
              width: "500px",
              maxWidth: "75%",
              maxHeight: "75%",
              overflowY: "auto",
              border: "1px solid rgba(255, 255, 255, 0.1)",
            }}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          >
            <h2 style={{ fontSize: "1.5rem", fontWeight: 600, color: "#f1f5f9", marginBottom: "1.5rem" }}>Edit User</h2>
            <div style={{ display: "flex", flexDirection: "column", gap: "1rem", marginBottom: "1.5rem" }}>
              <div>
                <label
                  htmlFor="edit-user-first-name"
                  style={{ display: "block", color: "#94a3b8", fontSize: "0.875rem", marginBottom: "0.5rem" }}
                >
                  First Name
                </label>
                <input
                  id="edit-user-first-name"
                  type="text"
                  value={userToEdit.firstName}
                  onChange={(e) => setUserToEdit({ ...userToEdit, firstName: e.target.value })}
                  style={{
                    width: "100%",
                    padding: "0.5rem",
                    background: "rgba(255, 255, 255, 0.05)",
                    border: "1px solid rgba(255, 255, 255, 0.1)",
                    borderRadius: "6px",
                    color: "#e2e8f0",
                  }}
                />
              </div>
              <div>
                <label
                  htmlFor="edit-user-last-name"
                  style={{ display: "block", color: "#94a3b8", fontSize: "0.875rem", marginBottom: "0.5rem" }}
                >
                  Last Name
                </label>
                <input
                  id="edit-user-last-name"
                  type="text"
                  value={userToEdit.lastName}
                  onChange={(e) => setUserToEdit({ ...userToEdit, lastName: e.target.value })}
                  style={{
                    width: "100%",
                    padding: "0.5rem",
                    background: "rgba(255, 255, 255, 0.05)",
                    border: "1px solid rgba(255, 255, 255, 0.1)",
                    borderRadius: "6px",
                    color: "#e2e8f0",
                  }}
                />
              </div>
              <div>
                <label
                  htmlFor="edit-user-email"
                  style={{ display: "block", color: "#94a3b8", fontSize: "0.875rem", marginBottom: "0.5rem" }}
                >
                  Email
                </label>
                <input
                  id="edit-user-email"
                  type="email"
                  value={userToEdit.email}
                  onChange={(e) => setUserToEdit({ ...userToEdit, email: e.target.value })}
                  style={{
                    width: "100%",
                    padding: "0.5rem",
                    background: "rgba(255, 255, 255, 0.05)",
                    border: "1px solid rgba(255, 255, 255, 0.1)",
                    borderRadius: "6px",
                    color: "#e2e8f0",
                  }}
                />
              </div>
              <div>
                <label
                  htmlFor="edit-user-status"
                  style={{ display: "block", color: "#94a3b8", fontSize: "0.875rem", marginBottom: "0.5rem" }}
                >
                  Status
                </label>
                <select
                  id="edit-user-status"
                  value={userToEdit.status}
                  onChange={(e) => setUserToEdit({ ...userToEdit, status: e.target.value })}
                  style={{
                    width: "100%",
                    padding: "0.5rem",
                    background: "rgba(255, 255, 255, 0.05)",
                    border: "1px solid rgba(255, 255, 255, 0.1)",
                    borderRadius: "6px",
                    color: "#e2e8f0",
                  }}
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
              <div>
                <label
                  htmlFor="edit-user-trading"
                  style={{ display: "block", color: "#94a3b8", fontSize: "0.875rem", marginBottom: "0.5rem" }}
                >
                  Trading Allowed
                </label>
                <select
                  id="edit-user-trading"
                  value={userToEdit.isTradingAllowed ? "yes" : "no"}
                  onChange={(e) => setUserToEdit({ ...userToEdit, isTradingAllowed: e.target.value === "yes" })}
                  style={{
                    width: "100%",
                    padding: "0.5rem",
                    background: "rgba(255, 255, 255, 0.05)",
                    border: "1px solid rgba(255, 255, 255, 0.1)",
                    borderRadius: "6px",
                    color: "#e2e8f0",
                  }}
                >
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                </select>
              </div>
            </div>
            <div style={{ display: "flex", gap: "1rem" }}>
              <button
                type="button"
                onClick={() => {
                  setShowEditUserModal(false)
                  setUserToEdit(null)
                }}
                style={{
                  flex: 1,
                  padding: "0.75rem",
                  background: "rgba(255, 255, 255, 0.05)",
                  border: "1px solid rgba(255, 255, 255, 0.1)",
                  borderRadius: "8px",
                  color: "#e2e8f0",
                  fontSize: "0.875rem",
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveUser}
                style={{
                  flex: 1,
                  padding: "0.75rem",
                  background: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
                  border: "none",
                  borderRadius: "8px",
                  color: "white",
                  fontSize: "0.875rem",
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {showResetPasswordModal && userToResetPassword && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0, 0, 0, 0.7)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
          onClick={() => setShowResetPasswordModal(false)}
          onKeyDown={(e) => {
            if (e.key === "Escape") setShowResetPasswordModal(false)
          }}
        >
          <div
            style={{
              background: "rgba(15, 23, 42, 0.95)",
              padding: "2rem",
              borderRadius: "12px",
              width: "500px",
              maxWidth: "75%",
              maxHeight: "75%",
              overflowY: "auto",
              border: "1px solid rgba(255, 255, 255, 0.1)",
            }}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          >
            <h2 style={{ fontSize: "1.5rem", fontWeight: 600, color: "#f1f5f9", marginBottom: "0.5rem" }}>
              Reset Password
            </h2>
            <p style={{ color: "#94a3b8", marginBottom: "1.5rem" }}>
              Reset password for <strong style={{ color: "#e2e8f0" }}>{userToResetPassword.name}</strong>
            </p>
            <div style={{ marginBottom: "1.5rem" }}>
              <label
                htmlFor="reset-password-input"
                style={{ display: "block", color: "#94a3b8", fontSize: "0.875rem", marginBottom: "0.5rem" }}
              >
                New Password
              </label>
              <input
                id="reset-password-input"
                type="text"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password"
                style={{
                  width: "100%",
                  padding: "0.5rem",
                  background: "rgba(255, 255, 255, 0.05)",
                  border: "1px solid rgba(255, 255, 255, 0.1)",
                  borderRadius: "6px",
                  color: "#e2e8f0",
                }}
              />
            </div>
            <div style={{ display: "flex", gap: "1rem" }}>
              <button
                type="button"
                onClick={() => {
                  setShowResetPasswordModal(false)
                  setUserToResetPassword(null)
                  setNewPassword("")
                }}
                style={{
                  flex: 1,
                  padding: "0.75rem",
                  background: "rgba(255, 255, 255, 0.05)",
                  border: "1px solid rgba(255, 255, 255, 0.1)",
                  borderRadius: "8px",
                  color: "#e2e8f0",
                  fontSize: "0.875rem",
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmResetPassword}
                disabled={!newPassword}
                style={{
                  flex: 1,
                  padding: "0.75rem",
                  background: newPassword
                    ? "linear-gradient(135deg, #eab308 0%, #ca8a04 100%)"
                    : "rgba(100, 116, 139, 0.3)",
                  border: "none",
                  borderRadius: "8px",
                  color: "white",
                  fontSize: "0.875rem",
                  fontWeight: 600,
                  cursor: newPassword ? "pointer" : "not-allowed",
                }}
              >
                Reset Password
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
